import React, { useState } from 'react';
import { X, User, Building2 } from 'lucide-react';

// Tipos de identificación según Siigo
const idTypes = {
  company: [
    { code: '31', name: 'NIT' },
    { code: '13', name: 'Cédula de ciudadanía' },
    { code: '22', name: 'Cédula de extranjería' },
    { code: '41', name: 'Pasaporte' },
    { code: '42', name: 'Documento de identificación extranjero' },
    { code: '50', name: 'NIT de otro país' }
  ],
  person: [
    { code: '13', name: 'Cédula de ciudadanía' },
    { code: '22', name: 'Cédula de extranjería' },
    { code: '11', name: 'Registro civil' },
    { code: '12', name: 'Tarjeta de identidad' },
    { code: '21', name: 'Tarjeta de extranjería' },
    { code: '41', name: 'Pasaporte' },
    { code: '42', name: 'Documento de identificación extranjero' },
    { code: '47', name: 'PEP' },
    { code: '48', name: 'PPT' }
  ]
};

// Responsabilidades fiscales según Siigo
const fiscalResponsibilities = [
  { code: 'R-99-PN', name: 'No aplica - Otros' },
  { code: 'O-13', name: 'Gran contribuyente' },
  { code: 'O-15', name: 'Autoretenedor' },
  { code: 'O-23', name: 'Agente de retención IVA' },
  { code: 'O-47', name: 'Régimen simple de tributación' },
  { code: 'R-04-PN', name: 'Impuesto sobre las ventas - IVA' }
];

const colombianCities = [
  { name: 'Bogotá', state_code: '11', city_code: '11001' },
  { name: 'Medellín', state_code: '05', city_code: '05001' },
  { name: 'Cali', state_code: '76', city_code: '76001' },
  { name: 'Barranquilla', state_code: '08', city_code: '08001' },
  { name: 'Cartagena', state_code: '13', city_code: '13001' },
  { name: 'Cúcuta', state_code: '54', city_code: '54001' },
  { name: 'Bucaramanga', state_code: '68', city_code: '68001' },
  { name: 'Pereira', state_code: '66', city_code: '66001' },
  { name: 'Santa Marta', state_code: '47', city_code: '47001' },
  { name: 'Ibagué', state_code: '73', city_code: '73001' },
  { name: 'Pasto', state_code: '52', city_code: '52001' },
  { name: 'Manizales', state_code: '17', city_code: '17001' },
  { name: 'Neiva', state_code: '41', city_code: '41001' },
  { name: 'Villavicencio', state_code: '50', city_code: '50001' },
  { name: 'Armenia', state_code: '63', city_code: '63001' },
  { name: 'Valledupar', state_code: '20', city_code: '20001' },
  { name: 'Montería', state_code: '23', city_code: '23001' },
  { name: 'Sincelejo', state_code: '70', city_code: '70001' },
  { name: 'Popayán', state_code: '19', city_code: '19001' },
  { name: 'Tunja', state_code: '15', city_code: '15001' },
  { name: 'Florencia', state_code: '18', city_code: '18001' },
  { name: 'Riohacha', state_code: '44', city_code: '44001' },
  { name: 'Quibdó', state_code: '27', city_code: '27001' },
  { name: 'Arauca', state_code: '81', city_code: '81001' },
  { name: 'Yopal', state_code: '85', city_code: '85001' },
  { name: 'San Andrés', state_code: '88', city_code: '88001' }
];

const calcNitDv = (nit) => {
  const weights = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];
  const digits = nit.replace(/\D/g, '').split('').reverse();
  const sum = digits.reduce((acc, d, i) => acc + parseInt(d, 10) * weights[i], 0);
  const r = sum % 11;
  return String(r > 1 ? 11 - r : r);
};

const NewClientModal = ({ isOpen, onClose, onSave }) => {
  const [clientType, setClientTypeState] = useState('company');
  const [formData, setFormData] = useState({
    id_type: '31',
    identification: '',
    check_digit: '',
    name: '',
    first_name: '',
    last_name: '',
    city: '',
    address: '',
    phone_indicative: '601',
    phone_number: '',
    phone_extension: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    fiscal_responsibility: 'R-99-PN',
    vat_responsible: false
  });

  if (!isOpen) return null;

  const setClientType = (type) => {
    setClientTypeState(type);
    setFormData(prev => ({ ...prev, id_type: type === 'company' ? '31' : '13' }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'identification' || name === 'id_type') {
        next.check_digit = next.id_type === '31' && next.identification ? calcNitDv(next.identification) : '';
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const allowed = idTypes[clientType].map(t => t.code);
    if (!allowed.includes(formData.id_type)) {
      alert('Seleccione un tipo de identificación válido');
      return;
    }

    if (!/^\d+$/.test(formData.identification) && !['41', '42', '50'].includes(formData.id_type)) {
      alert('La identificación solo debe contener números (sin puntos, guiones ni dígito de verificación)');
      return;
    }

    if (!formData.city || !formData.address || !formData.phone_number || !formData.contact_email) {
      alert('Ciudad, dirección, teléfono y correo son obligatorios en Siigo');
      return;
    }
    if (!formData.identification) {
      alert('Por favor ingrese el número de identificación');
      return;
    }
    
    if (clientType === 'company' && !formData.name) {
      alert('Por favor ingrese la razón social');
      return;
    }
    
    if (clientType === 'person' && (!formData.first_name || !formData.last_name)) {
      alert('Por favor ingrese nombres y apellidos');
      return;
    }

    const selectedCity = colombianCities.find(c => c.name === formData.city);
    
    const customerData = {
      type: 'Customer',
      person_type: clientType === 'company' ? 'Company' : 'Person',
      id_type: formData.id_type,
      identification: formData.identification.trim(),
      check_digit: formData.id_type === '31' ? formData.check_digit : undefined,
      name: clientType === 'company' 
        ? [formData.name].filter(Boolean)
        : [formData.first_name, formData.last_name].filter(Boolean),
      commercial_name: clientType === 'company' ? formData.name : undefined,
      branch_office: 0,
      active: true,
      vat_responsible: formData.vat_responsible,
      fiscal_responsibilities: [{ code: formData.fiscal_responsibility }],
      address: {
        address: formData.address,
        city: {
          country_code: 'Co',
          state_code: selectedCity.state_code,
          city_code: selectedCity.city_code
        }
      },
      phones: [{
        indicative: formData.phone_indicative,
        number: formData.phone_number,
        extension: formData.phone_extension || ''
      }],
      contacts: [{
        first_name: formData.contact_first_name || (clientType === 'person' ? formData.first_name : 'Contacto'),
        last_name: formData.contact_last_name || (clientType === 'person' ? formData.last_name : 'Principal'),
        email: formData.contact_email.trim(),
        phone: {
          indicative: formData.phone_indicative,
          number: formData.phone_number
        }
      }]
    };

    await onSave(customerData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <User size={28} />
            Crear Nuevo Tercero (Cliente)
          </h2>
          <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <form onSubmit={handleSubmit}>
            {/* Selector de tipo */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-3 text-gray-700">Tipo *</label>
              <div className="grid grid-cols-2 gap-4">
                <button type="button" data-testid="new-client-type-company" onClick={() => setClientType('company')}
                  className={`p-4 rounded-lg border-2 flex items-center justify-center gap-2 font-semibold transition-all ${
                    clientType === 'company' ? 'border-blue-500 bg-blue-100 text-blue-700' : 'border-gray-300 hover:border-blue-300'
                  }`}>
                  <Building2 size={20} /> Empresa
                </button>
                <button type="button" data-testid="new-client-type-person" onClick={() => setClientType('person')}
                  className={`p-4 rounded-lg border-2 flex items-center justify-center gap-2 font-semibold transition-all ${
                    clientType === 'person' ? 'border-purple-500 bg-purple-100 text-purple-700' : 'border-gray-300 hover:border-purple-300'
                  }`}>
                  <User size={20} /> Persona
                </button>
              </div>
            </div>

            {/* Tipo de identificación */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Tipo de identificación *</label>
                <select name="id_type" data-testid="new-client-id-type" value={formData.id_type} onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" required>
                  {idTypes[clientType].map(type => (
                    <option key={type.code} value={type.code}>{type.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Identificación *</label>
                <input type="text" name="identification" data-testid="new-client-identification" value={formData.identification} onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" required />
              </div>
            </div>

            {/* Dígito de verificación (solo para NIT) */}
            {formData.id_type === '31' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-gray-700">Dv (Dígito de verificación)</label>
                <input type="text" name="check_digit" data-testid="new-client-dv" value={formData.check_digit} onChange={handleInputChange} maxLength="1"
                  className="w-32 p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" placeholder="0-9" />
              </div>
            )}

            {/* Razón social o Nombres y Apellidos */}
            {clientType === 'company' ? (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-gray-700">Razón social *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500" required />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Nombres *</label>
                  <input type="text" name="first_name" value={formData.first_name} onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Apellidos *</label>
                  <input type="text" name="last_name" value={formData.last_name} onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" required />
                </div>
              </div>
            )}

            {/* Ciudad y Dirección */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Ciudad *</label>
                <select name="city" data-testid="new-client-city" value={formData.city} onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500">
                  <option value="">Seleccionar ciudad...</option>
                  {colombianCities.map(city => (
                    <option key={city.name} value={city.name}>{city.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Dirección *</label>
                <input type="text" name="address" data-testid="new-client-address" value={formData.address} onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" />
              </div>
            </div>

            {/* Teléfono */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Indicativo</label>
                <input type="text" name="phone_indicative" value={formData.phone_indicative} onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" placeholder="601" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700"># de Teléfono *</label>
                <input type="text" name="phone_number" data-testid="new-client-phone" value={formData.phone_number} onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" placeholder="3001234567" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Extensión</label>
                <input type="text" name="phone_extension" value={formData.phone_extension} onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" placeholder="123" />
              </div>
            </div>

            {/* Datos para facturación y envío */}
            <div className="border-t-2 border-gray-300 pt-6 mt-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">📋 Datos para facturación y envío</h3>
              
              {/* Responsabilidad fiscal */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-gray-700">Responsabilidad fiscal</label>
                <select name="fiscal_responsibility" value={formData.fiscal_responsibility} onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500">
                  {fiscalResponsibilities.map(resp => (
                    <option key={resp.code} value={resp.code}>{resp.name}</option>
                  ))}
                </select>
              </div>

              {/* Responsable de IVA */}
              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" name="vat_responsible" checked={formData.vat_responsible} onChange={handleInputChange}
                    className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500" />
                  <span className="text-sm font-medium text-gray-700">Responsable de IVA</span>
                </label>
              </div>

              {/* Contacto para facturación */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Nombres del contacto</label>
                  <input type="text" name="contact_first_name" value={formData.contact_first_name} onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Apellidos del contacto</label>
                  <input type="text" name="contact_last_name" value={formData.contact_last_name} onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-gray-700">Correo electrónico cuando facture *</label>
                <input type="email" name="contact_email" data-testid="new-client-email" value={formData.contact_email} onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" placeholder="email@ejemplo.com" />
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-4 mt-6 pt-6 border-t border-gray-300">
              <button type="button" onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition-colors">
                Cancelar
              </button>
              <button type="submit" data-testid="new-client-submit"
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-colors">
                Crear Tercero en Siigo
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewClientModal;
